import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Calendar, ChevronRight, UserCheck } from "@tamagui/lucide-icons";
import { Timestamp } from "firebase/firestore";
import { observer } from "mobx-react-lite";
import React, { type PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { View, XStack, YStack } from "tamagui";
import { useApi } from "../../core/apis";
import { DatePicker } from "../../core/components/FormDataModal";
import { setDayJsLocale } from "../../core/dayJs";
import { useStores } from "../../core/stores";
import { Permission } from "../../permissions/types";
import { UserProvider } from "../../permissions/UserProvider";
import { PlanInfoBox } from "../../plans/PlanInfoBox";
import { usePlanNavigation } from "../../plans/usePlanNavigation";
import { Body } from "../../ui/Body";
import { Footer } from "../../ui/Footer";
import { Label } from "../../ui/Label";
import { FullScreenLoading } from "../../ui/Loading";
import { Screen } from "../../ui/Screen";
import { Title } from "../../ui/Title";
import { UserAvatarGroupRaw } from "../../ui/UserAvatarGroupRaw";
import type UserModel from "../../users/UserModel";
import { BusinessPlanMenu } from "../components/BusinessPlanMenu";
import { BusinessUserInfoSheet } from "../components/BusinessUserInfoSheet";
import { BusinessDemoFooter } from "./BusinessDemoFooter";
import { BusinessAdmin } from "./BusinessTempAdmin";
import { BusinessGate } from "./ui/BusinessGate";
import { BusinessNextUp } from "./ui/BusinessNextUp";
import { BusinessScheduleNextUp } from "./ui/BusinessScheduleNextUp";

type Props = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  | "BusinessHome"
  | "BusinessIntro"
  | "BusinessFeedItem"
  | "BusinessSchedule"
  | "BusinessScheduleItem"
  | "BusinessPlan"
  | "BusinessMembers"
  | "BusinessProfile"
  | "BusinessSection"
  | "BusinessSectionModule"
  | "BusinessTasks"
  | "BusinessTasksModule"
  | "BusinessTeamModule"
  | "BusinessTeam"
  | "BusinessSettings"
>;

export const BusinessPlanScreen = observer(
  ({
    route,
    children,
    showAvatars,
    showInfo,
    hideMenu,
    hideIntroTip,
  }: PropsWithChildren<Props> & {
    showAvatars?: boolean;
    showInfo?: boolean;
    hideMenu?: boolean;
    hideIntroTip?: boolean;
  }) => {
    const businessPlanUid = route.params.businessUid;
    const { t, i18n } = useTranslation();
    const navigation = usePlanNavigation();
    const { businessPlansStore, sessionStore } = useStores();
    const { businessPlansApi, plansApi, usersApi } = useApi();

    const businessPlan = businessPlansStore.byId.get(businessPlanUid);
    const user = businessPlan?.userInPlan;
    const userPlanUid = user?.data.planUid;

    const [userLoaded, setUserLoaded] = React.useState(!!user);

    React.useEffect(() => {
      if (sessionStore.isLoggedIn && userPlanUid) {
        const unsub = plansApi.watchItem(userPlanUid);
        return () => {
          unsub();
        };
      }
    }, [plansApi, sessionStore, userPlanUid]);

    React.useEffect(() => {
      if (sessionStore.isLoggedIn) {
        const unsub = businessPlansApi.watchItem(businessPlanUid);
        return () => {
          unsub();
        };
      }
    }, [businessPlanUid, businessPlansApi, sessionStore.isLoggedIn]);

    React.useEffect(() => {
      let unsubscribe: (() => void) | undefined;

      async function fetchUserAndWatch() {
        const u: UserModel | undefined = await usersApi.getBusinessUser(
          sessionStore.uid,
          businessPlanUid,
        );

        if (u) {
          unsubscribe = usersApi.watchItem(u.uid);
        }
        setUserLoaded(true);
      }

      if (sessionStore.uid && businessPlan && !user) {
        void fetchUserAndWatch();
      }

      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }, [usersApi, user, businessPlan, businessPlanUid, sessionStore.uid]);

    // Set language
    React.useEffect(() => {
      const lng = businessPlan?.data.eventInfo.settings?.useLocale;
      if (businessPlan && lng && lng !== i18n.language) {
        if (
          i18n.isInitialized &&
          i18n.options.supportedLngs &&
          i18n.options.supportedLngs?.includes(lng)
        ) {
          i18n.changeLanguage(lng);
          setDayJsLocale(lng);
        }
      }
    }, [businessPlan, i18n]);

    if (!sessionStore.isLoggedIn || !businessPlan || !userLoaded) {
      return <FullScreenLoading subtitle={t("business:event_loading")} />;
    }

    if (userLoaded && !businessPlan?.userInPlan) {
      return (
        <Screen theme={businessPlan?.data.customerInfo.theme}>
          <YStack gap="$medium">
            <BusinessAdmin businessPlanUid={businessPlanUid} />
            {businessPlan && <BusinessGate businessPlan={businessPlan} />}
          </YStack>
        </Screen>
      );
    }

    const showDatePicker =
      businessPlan.checkPermission(Permission.AccessToAdminPage) &&
      !businessPlan.data.eventInfo.date;

    return (
      <UserProvider
        userUid={businessPlan.userUidInPlan}
        businessPlan={businessPlan}
      >
        <Screen theme={businessPlan.data.customerInfo.theme}>
          {!hideMenu && <BusinessPlanMenu businessPlan={businessPlan} />}

          {showInfo && (
            <>
              <View>
                <PlanInfoBox
                  hideEdit={
                    !businessPlan.checkPermission(Permission.AccessToAdminPage)
                  }
                  info={businessPlan.data.planInfo}
                  onEditPress={() => {
                    navigation.navigate("BusinessSettings", {
                      businessUid: businessPlanUid,
                      tab: "info",
                    });
                  }}
                />
                <BusinessNextUp businessPlan={businessPlan} />
              </View>
              <BusinessScheduleNextUp businessPlan={businessPlan} />
            </>
          )}

          {!hideIntroTip && !businessPlan.hasCompletedIntroduction && (
            <XStack
              paddingVertical="$medium"
              justifyContent={"space-between"}
              gap={"$medium"}
              cursor={"pointer"}
              alignItems={"center"}
              onPress={() => {
                navigation.navigate("BusinessIntro", {
                  businessUid: businessPlan.uid,
                });
              }}
              paddingHorizontal="$medium"
              $gtSm={{ paddingHorizontal: 0 }}
              width={"100%"}
            >
              <XStack gap={"$medium"} alignItems={"center"}>
                <UserCheck size={"$medium"} />
                <YStack gap={"$small"}>
                  <Label>{t("business:when_ready")}</Label>
                  <Body numberOfLines={1} muted>
                    {t("business:complete_profile")}
                  </Body>
                </YStack>
              </XStack>
              <View width={"$medium"}>
                <ChevronRight />
              </View>
            </XStack>
          )}

          {showAvatars && (
            <UserAvatarGroupRaw
              businessPlan={businessPlan}
              users={businessPlan.planUsersSorted}
              onPress={() => {
                navigation.navigate("BusinessMembers", {
                  businessUid: businessPlanUid,
                });
              }}
            />
          )}

          {children}

          <Footer />

          {showDatePicker && (
            <DatePicker
              header={
                <YStack gap="$space.medium">
                  <XStack gap="$medium" alignItems="center">
                    <Calendar />
                    <Title>Set Event date</Title>
                  </XStack>
                </YStack>
              }
              open={showDatePicker}
              sheetId="dateDuePickerSet"
              onSelect={(date) => {
                businessPlan.updateEventInfo({
                  date: Timestamp.fromDate(new Date(date)),
                });
              }}
            />
          )}

          <BusinessUserInfoSheet
            sheetId={"b_userinfo"}
            businessPlan={businessPlan}
            userInfo={businessPlan.showUserInfoSheet}
            open={!!businessPlan.showUserInfoSheet}
            onOpenChange={() => {
              businessPlan.setShowUserInfo(null);
            }}
          />

          <BusinessDemoFooter businessPlanUid={businessPlanUid} />
          <BusinessAdmin businessPlanUid={businessPlanUid} />
        </Screen>
      </UserProvider>
    );
  },
);

export default BusinessPlanScreen;
